import { createHttpClient, isHttpError, type HttpClient } from '../lib/http'

import type {
  ApiResponse,
  AvailableJobTimeSlotDTO,
  CancelWidgetCheckoutRequest,
  CancelWidgetCheckoutResponse,
  ConfirmWidgetCheckoutSessionResponse,
  CreateWidgetBookingRequest,
  CreateWidgetBookingResult,
  CreateWidgetCheckoutSessionRequest,
  CreateWidgetCheckoutSessionResponse,
  LocalDateString,
  MonthAvailabilityDTO,
  ResolveWidgetVehicleRequest,
  ResolveWidgetVehicleResponse,
  UUID,
  WidgetBootstrapResponse,
  WidgetFramedParent,
  WidgetVehicleTypeRequest,
  WidgetVehicleTypeResponse,
} from '../types/PublicBookingChannelTypes'

export interface PublicBookingChannelServiceOptions {
  apiUrl: string
  withCredentials?: boolean
}

export class PublicBookingChannelService {
  private readonly client: HttpClient

  constructor(options: PublicBookingChannelServiceOptions) {
    this.client = createHttpClient({
      baseURL: options.apiUrl,
      withCredentials: options.withCredentials ?? true,
    })
  }

  async bootstrap(publicKey: string): Promise<WidgetBootstrapResponse> {
    const res = await this.client.get<ApiResponse<WidgetBootstrapResponse>>(
      `/public/widgets/${encodeURIComponent(publicKey)}/bootstrap`
    )
    return res.data.data
  }

  async availability(args: {
    publicKey: string
    bookingDate: LocalDateString | Date
    serviceIds?: UUID[]
    durationMinutes?: number
    token?: string
    authorization?: string
  }): Promise<AvailableJobTimeSlotDTO[]> {
    const { publicKey, bookingDate, serviceIds, durationMinutes } = args

    const bookingDateIso =
      bookingDate instanceof Date
        ? toLocalDateString(bookingDate)
        : bookingDate

    const params = new URLSearchParams()
    params.set('bookingDate', bookingDateIso)
    if (typeof durationMinutes === 'number') {
      params.set('durationMinutes', String(durationMinutes))
    }
    if (serviceIds?.length) {
      for (const id of serviceIds) params.append('serviceIds', id)
    }

    const res = await this.client.get<ApiResponse<AvailableJobTimeSlotDTO[]>>(
      `/public/widgets/${encodeURIComponent(publicKey)}/availability`,
      {
        params,
        headers: buildAuthHeaders(args),
      }
    )

    return res.data.data
  }

  /**
   * Per-day capacity for a whole month, so the calendar can show availability
   * before the customer clicks anything.
   *
   * Older backends do not expose this endpoint; callers treat a failure as
   * "no capacity information" rather than as an error.
   */
  async monthAvailability(args: {
    publicKey: string
    /** `YYYY-MM`. */
    month: string
    serviceIds?: UUID[]
    durationMinutes?: number
    token?: string
    authorization?: string
  }): Promise<MonthAvailabilityDTO> {
    const params = new URLSearchParams()
    params.set('month', args.month)
    if (typeof args.durationMinutes === 'number') {
      params.set('durationMinutes', String(args.durationMinutes))
    }
    if (args.serviceIds?.length) {
      for (const id of args.serviceIds) params.append('serviceIds', id)
    }

    const res = await this.client.get<ApiResponse<MonthAvailabilityDTO>>(
      `/public/widgets/${encodeURIComponent(args.publicKey)}/availability/month`,
      {
        params,
        headers: buildAuthHeaders(args),
      }
    )

    return res.data.data
  }

  /**
   * Tyres available in a given size, priced fully fitted.
   *
   * Served from whichever supplier the channel is configured against. Callers
   * treat a failure as "no products to show" and fall back to booking the
   * fitting with the size recorded, rather than blocking the booking.
   */
  async tyreOffers(args: {
    publicKey: string
    size: string
    quantity?: number
    /** A staggered car's rear size: the answer then carries an axle on every offer. */
    rearSize?: string | null
    rearQuantity?: number | null
    token?: string
    authorization?: string
  }): Promise<unknown[]> {
    const params = new URLSearchParams()
    params.set('size', args.size)
    if (typeof args.quantity === 'number') params.set('quantity', String(args.quantity))
    if (args.rearSize) {
      params.set('rearSize', args.rearSize)
      if (typeof args.rearQuantity === 'number') {
        params.set('rearQuantity', String(args.rearQuantity))
      }
    }

    const res = await this.client.get<ApiResponse<unknown[]>>(
      `/public/widgets/${encodeURIComponent(args.publicKey)}/tyres`,
      {
        params,
        headers: buildAuthHeaders(args),
      }
    )

    return Array.isArray(res.data.data) ? res.data.data : []
  }

  /**
   * Creates a booking. The response says whether it is confirmed, waiting on
   * the garage, or waiting on payment, and carries the reference to show.
   *
   * Fails with 409 `SLOT_UNAVAILABLE` when the time went while the customer
   * was filling in their details.
   *
   * The bundle is served as "latest", so it can reach a backend that predates
   * booking-requests. A 404 there falls back to the legacy `/bookings`
   * endpoint with the same body; it answers with a bare booking id, so every
   * other field comes back null and the flow decides from the channel plan.
   */
  async createBookingRequest(args: {
    publicKey: string
    body: CreateWidgetBookingRequest
    token?: string
    authorization?: string
  }): Promise<CreateWidgetBookingResult> {
    const base = `/public/widgets/${encodeURIComponent(args.publicKey)}`
    const config = { headers: buildAuthHeaders(args) }
    try {
      const res = await this.client.post<ApiResponse<CreateWidgetBookingResult>>(
        `${base}/booking-requests`,
        args.body,
        config
      )
      return res.data.data
    } catch (error) {
      if (!isHttpError(error) || error.response?.status !== 404) throw error
    }

    const legacy = await this.client.post<ApiResponse<UUID>>(`${base}/bookings`, args.body, config)
    return {
      bookingId: legacy.data.data,
      reference: null,
      status: null,
      payment: null,
      slotStart: null,
      slotEnd: null,
    }
  }

  /**
   * Opens Stripe Checkout for a booking awaiting payment. Called again for the
   * same booking on a retry: the backend hands back the open session, or a new
   * one, rather than a second booking.
   *
   * `returnUrl` is the page to come back to. The backend only honours it when
   * its origin is one of the channel's allowed origins.
   */
  async createBookingCheckoutSession(args: {
    publicKey: string
    bookingId: UUID
    returnUrl?: string | null
    /** The widget's own page, when it runs inside a frame (ACA-99). */
    fallbackReturnUrl?: string | null
    /** Whether a framed widget knows the exact page around it (ACA-111). */
    framedParent?: WidgetFramedParent | null
    /** The visit's traffic source (ACA-107), for a Checkout that ends on the hosted page. */
    trafficSource?: string | null
    token?: string
    authorization?: string
  }): Promise<CreateWidgetCheckoutSessionResponse> {
    const body: CreateWidgetCheckoutSessionRequest = args.returnUrl ? { returnUrl: args.returnUrl } : {}
    if (args.fallbackReturnUrl) body.fallbackReturnUrl = args.fallbackReturnUrl
    if (args.framedParent) body.framedParent = args.framedParent
    if (args.trafficSource) body.trafficSource = args.trafficSource

    const res = await this.client.post<ApiResponse<CreateWidgetCheckoutSessionResponse>>(
      `/public/widgets/${encodeURIComponent(args.publicKey)}/bookings/${encodeURIComponent(args.bookingId)}/checkout-session`,
      body,
      {
        headers: buildAuthHeaders(args),
      }
    )
    return res.data.data
  }

  async confirmBookingPayment(args: {
    publicKey: string
    bookingId: UUID
    sessionId: string
    token?: string
    authorization?: string
  }): Promise<ConfirmWidgetCheckoutSessionResponse> {
    const params = new URLSearchParams()
    params.set('sessionId', args.sessionId)

    const res = await this.client.get<ApiResponse<ConfirmWidgetCheckoutSessionResponse>>(
      `/public/widgets/${encodeURIComponent(args.publicKey)}/bookings/${encodeURIComponent(args.bookingId)}/confirm-payment`,
      {
        params,
        headers: buildAuthHeaders(args),
      }
    )
    return res.data.data
  }

  /**
   * The customer came back from Checkout without paying: the server releases
   * the hold now, so the time is free again (ACA-101). Never cancels a paid
   * booking; repeating it changes nothing.
   */
  async cancelBookingCheckout(args: {
    publicKey: string
    bookingId: UUID
    sessionId?: string | null
    token?: string
    authorization?: string
  }): Promise<CancelWidgetCheckoutResponse> {
    const body: CancelWidgetCheckoutRequest = args.sessionId ? { sessionId: args.sessionId } : {}

    const res = await this.client.post<ApiResponse<CancelWidgetCheckoutResponse>>(
      `/public/widgets/${encodeURIComponent(args.publicKey)}/bookings/${encodeURIComponent(args.bookingId)}/checkout-cancel`,
      body,
      {
        headers: buildAuthHeaders(args),
      }
    )
    return res.data.data
  }

  /** One funnel step (ACA-107): an event name and a traffic source, nothing else. */
  async recordEvent(args: {
    publicKey: string
    event: string
    source: string
    token?: string
    authorization?: string
  }): Promise<void> {
    await this.client.post(
      `/public/widgets/${encodeURIComponent(args.publicKey)}/events`,
      { event: args.event, source: args.source },
      { headers: buildAuthHeaders(args) }
    )
  }

  async resolveVehicle(args: {
    publicKey: string
    body: ResolveWidgetVehicleRequest
    token?: string
    authorization?: string
  }): Promise<ResolveWidgetVehicleResponse> {
    const res = await this.client.post<ApiResponse<ResolveWidgetVehicleResponse>>(
      `/public/widgets/${encodeURIComponent(args.publicKey)}/vehicle`,
      args.body,
      {
        headers: buildAuthHeaders(args),
      }
    )
    return res.data.data
  }

  /**
   * The customer's answer to "What is it?" (ACA-110) for the plate this widget
   * just looked up. Fails with 404 `VEHICLE_LOOKUP_NEEDED` when the server holds
   * no lookup of that plate by this widget (a new token, or a day gone): look it
   * up again, then answer again.
   */
  async answerVehicleType(args: {
    publicKey: string
    body: WidgetVehicleTypeRequest
    token?: string
    authorization?: string
  }): Promise<WidgetVehicleTypeResponse> {
    const res = await this.client.post<ApiResponse<WidgetVehicleTypeResponse>>(
      `/public/widgets/${encodeURIComponent(args.publicKey)}/vehicle/type`,
      args.body,
      {
        headers: buildAuthHeaders(args),
      }
    )
    return res.data.data
  }
}

function buildAuthHeaders(args: {
  token?: string
  authorization?: string
}): Record<string, string> {
  if (args.authorization) return { Authorization: args.authorization }
  if (args.token) return { 'X-Widget-Token': args.token }
  return {}
}

function toLocalDateString(date: Date): LocalDateString {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
